import { Copy, Hash, MoreHorizontal } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { CopyMenuItem } from "./CopyMenuItem";
import { CopyTurnLinkButton } from "./CopyTurnLinkButton";
import { formatTurnDay, formatTurnTime } from "./formatTurnTime";

interface TurnActionsMenuProps {
  // Turn creation time (ISO); the day (e.g. "Yesterday") shows to the left of
  // the button, with the full time (e.g. "Yesterday, 5:52 PM") in its tooltip,
  // in the viewer's local time zone.
  createdAt: string;
  // The agent's response text for this turn. A "Copy output" row appears when
  // it's non-empty; shown on every surface.
  outputText?: string;
  // Root turn anchor id for the copy-link permalink. When absent, the copy-link
  // row is omitted even if `showCopyLink` is set.
  turnId?: string;
  // Reveal the "Copy link to turn" row. On for the read-only shared/admin
  // views; off for the live regular conversation.
  showCopyLink?: boolean;
  // Trace id to quote to support, on turns worth reporting. Copied rather than
  // displayed — it's 36 characters of hex nobody reads.
  reference?: string;
}

// Message date plus a "More actions" (⋯) affordance, both revealed on turn
// hover. The button opens a popover with the copy actions each prop enables.
// Requires a `group/turn` ancestor to drive the hover reveal.
export const TurnActionsMenu = ({
  createdAt,
  outputText,
  turnId,
  showCopyLink = false,
  reference,
}: TurnActionsMenuProps) => {
  const day = formatTurnDay(createdAt);
  const time = formatTurnTime(createdAt);
  const hasOutput = !!outputText?.trim();
  return (
    <>
      {day && (
        <Tooltip>
          <TooltipTrigger asChild>
            <time
              dateTime={createdAt}
              className={cn(
                "cursor-default text-xs text-muted-foreground transition-opacity",
                "opacity-100 md:opacity-0 md:group-hover/turn:opacity-100"
              )}
            >
              {day}
            </time>
          </TooltipTrigger>
          <TooltipContent>
            <p>{time}</p>
          </TooltipContent>
        </Tooltip>
      )}
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className={cn(
                  "cursor-pointer rounded p-1 text-muted-foreground transition-[color,background-color,opacity]",
                  "hover:bg-muted hover:text-foreground",
                  "opacity-100 md:opacity-0 md:group-hover/turn:opacity-100 md:focus-visible:opacity-100"
                )}
              >
                <MoreHorizontal className="size-4" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>More actions</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent align="start" className="w-56 p-1.5">
          {hasOutput && (
            <CopyMenuItem
              getText={() => outputText ?? ""}
              idleLabel="Copy output"
              icon={Copy}
              successMessage="Output copied"
            />
          )}
          {reference && (
            <CopyMenuItem
              getText={() => reference}
              idleLabel="Copy reference"
              icon={Hash}
              successMessage="Reference copied"
            />
          )}
          {showCopyLink && turnId && <CopyTurnLinkButton turnId={turnId} />}
        </PopoverContent>
      </Popover>
    </>
  );
};
